import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import Page from "../../minimals/components/page";
import { UploadMultiFile } from "../components/upload";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PdfViewerWithLocation from "./PdfViewerWithLocation";
import ExtractedFieldsDisplay from "./ExtractedFieldsDisplay";
import { PsaAnalysisResponse, PsaFieldResult } from "./types";

const llmWindowSizeInPages = 30;
const llmWindowOverlapInPages = 5;

const buildWindowRanges = (totalPages: number) => {
  if (totalPages <= 0) {
    return [] as Array<{ start: number; end: number }>;
  }

  const ranges: Array<{ start: number; end: number }> = [];
  const step = Math.max(1, llmWindowSizeInPages - llmWindowOverlapInPages);

  for (let start = 1; start <= totalPages; start += step) {
    const end = Math.min(start + llmWindowSizeInPages - 1, totalPages);
    ranges.push({ start, end });
    if (end >= totalPages) {
      break;
    }
  }

  return ranges;
};

const getStatusMessage = (elapsedMs: number, totalPages: number) => {
  if (elapsedMs < 2000) {
    return {
      headline: "Getting things ready...",
      detail: "Uploading the file and opening the preview."
    };
  }

  if (elapsedMs < 6000) {
    return {
      headline: "Reading the document...",
      detail: "Checking the file so we can start reviewing it."
    };
  }

  const ranges = buildWindowRanges(totalPages || llmWindowSizeInPages);
  const callCount = Math.max(ranges.length + (ranges.length > 0 ? 1 : 0), 1);
  const reviewIndex = Math.min(Math.floor((elapsedMs - 6000) / 3200), callCount - 1);
  const range = ranges[Math.min(reviewIndex, Math.max(ranges.length - 1, 0))] ?? {
    start: 1,
    end: totalPages || llmWindowSizeInPages
  };
  const isFinalReview = reviewIndex >= ranges.length && ranges.length > 0;

  return {
    headline: isFinalReview ? "Double-checking the final results..." : `Reviewing section ${reviewIndex + 1} of ${ranges.length}...`,
    detail: isFinalReview
      ? "Confirming the details before showing the completed review."
      : `Looking through pages ${range.start}-${range.end} for names, property details, and key terms.`
  };
};

export default function PurchaseAgreementExtractor() {
  const params = new URLSearchParams(window.location.search);
  const channelId = params.get("channel");

  const [file, setFile] = useState<(File & { preview?: string }) | null>(null);
  const [analysis, setAnalysis] = useState<PsaAnalysisResponse | null>(null);
  const [activeField, setActiveField] = useState<PsaFieldResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStartedAt, setAnalysisStartedAt] = useState<number | null>(null);
  const [analysisTick, setAnalysisTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [feedbackValue, setFeedbackValue] = useState<boolean | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const status = useMemo(
    () => getStatusMessage((analysisStartedAt ? Date.now() - analysisStartedAt : 0) + analysisTick, analysis?.pageCount ?? 0),
    [analysis?.pageCount, analysisStartedAt, analysisTick]
  );

  useEffect(() => {
    if (!isAnalyzing || !analysisStartedAt) {
      return undefined;
    }

    const timer = window.setInterval(() => setAnalysisTick((value) => value + 1000), 1000);
    return () => window.clearInterval(timer);
  }, [isAnalyzing, analysisStartedAt]);

  const runAnalysis = useCallback(async (selectedFile: File) => {
    setAnalysis(null);
    setActiveField(null);
    setError(null);
    setFeedbackValue(null);
    setFeedbackText("");
    setFeedbackMessage(null);
    setFeedbackError(null);
    setIsAnalyzing(true);
    setAnalysisStartedAt(Date.now());
    setAnalysisTick(0);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const { data } = await axios.post<PsaAnalysisResponse>("/api/purchaseAgreementExtractor/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setAnalysis(data);
      setActiveField(null);
    } catch (requestError: any) {
      const message = requestError?.response?.data?.message || requestError?.response?.data?.error || "Something went wrong during extraction.";
      setError(message);
    } finally {
      setIsAnalyzing(false);
      setAnalysisStartedAt(null);
    }
  }, []);

  const submitFeedback = useCallback(
    async (isPositive: boolean) => {
      if (!analysis) {
        return;
      }

      if (!isPositive && !feedbackText.trim()) {
        setFeedbackError("Please add a quick note so we know what to fix.");
        return;
      }

      setFeedbackValue(isPositive);
      setFeedbackError(null);
      setFeedbackMessage(null);
      setIsSubmittingFeedback(true);

      try {
        await axios.post("/api/purchaseAgreementExtractor/feedback", {
          analysisId: analysis.analysisId,
          isPositive,
          feedbackText: isPositive ? null : feedbackText.trim(),
          originalFileName: analysis.fileName,
          analysisJson: JSON.stringify(analysis)
        });
        setFeedbackMessage("Thanks, feedback saved.");
      } catch (requestError: any) {
        const message =
          requestError?.response?.data?.message || requestError?.response?.data?.error || "Feedback could not be saved.";
        setFeedbackError(message);
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [analysis, feedbackText]
  );

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      const nextFile = Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0])
      }) as File & { preview: string };

      setFile((currentFile) => {
        if (currentFile?.preview) {
          URL.revokeObjectURL(currentFile.preview);
        }
        return nextFile;
      });

      void runAnalysis(nextFile);
    },
    [runAnalysis]
  );

  useEffect(() => {
    if (!channelId || file) {
      return undefined;
    }

    const channel = new BroadcastChannel(channelId);
    channel.postMessage({ type: "ready" });
    channel.onmessage = (event) => {
      const nextFile = Object.assign(event.data.file, {
        preview: URL.createObjectURL(event.data.file)
      }) as File & { preview: string };
      setFile(nextFile);
      void runAnalysis(nextFile);
    };

    return () => channel.close();
  }, [channelId, file, runAnalysis]);

  useEffect(() => {
    return () => {
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
    };
  }, [file]);

  return (
    <Page title="Purchase Agreement Extractor">
      <Stack direction="row" spacing={3} alignItems="flex-start">
        <Stack width={760} spacing={2}>
          <Typography variant="h4">Purchase Agreement Extractor</Typography>
          <Typography color="text.secondary">
            Upload a purchase agreement to extract the key details and review the matching text in the document.
          </Typography>

          <UploadMultiFile
            files={file ? [file] : []}
            onDrop={handleFileDrop}
            onRemove={() => {
              if (file?.preview) {
                URL.revokeObjectURL(file.preview);
              }
              setFile(null);
              setAnalysis(null);
              setActiveField(null);
              setError(null);
              setFeedbackValue(null);
              setFeedbackText("");
              setFeedbackMessage(null);
              setFeedbackError(null);
            }}
            onRemoveAll={() => {
              if (file?.preview) {
                URL.revokeObjectURL(file.preview);
              }
              setFile(null);
              setAnalysis(null);
              setActiveField(null);
              setError(null);
              setFeedbackValue(null);
              setFeedbackText("");
              setFeedbackMessage(null);
              setFeedbackError(null);
            }}
            showPreview={false}
            showSelectedFiles
            multiple={false}
          />

          {isAnalyzing && (
            <Alert icon={<CircularProgress size={18} />} severity="info">
              <Typography variant="subtitle2">{status.headline}</Typography>
              <Typography variant="body2">{status.detail}</Typography>
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <ExtractedFieldsDisplay
            analysis={analysis}
            activeFieldKey={activeField?.key ?? null}
            onSelectField={setActiveField}
          />

          {analysis && (
            <Paper elevation={2} sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="subtitle1">Was this extraction helpful?</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your feedback helps improve future purchase agreement reviews.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Helpful">
                      <span>
                        <IconButton
                          color={feedbackValue === true ? "primary" : "default"}
                          disabled={isSubmittingFeedback}
                          onClick={() => void submitFeedback(true)}
                        >
                          <ThumbUpIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Needs work">
                      <span>
                        <IconButton
                          color={feedbackValue === false ? "error" : "default"}
                          disabled={isSubmittingFeedback}
                          onClick={() => {
                            setFeedbackValue(false);
                            setFeedbackMessage(null);
                          }}
                        >
                          <ThumbDownIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                {feedbackValue === false && !feedbackMessage && (
                  <Stack spacing={1}>
                    <TextField
                      label="What should be corrected?"
                      value={feedbackText}
                      onChange={(event) => setFeedbackText(event.target.value)}
                      minRows={3}
                      multiline
                      fullWidth
                    />
                    <Box>
                      <Button
                        variant="contained"
                        disabled={isSubmittingFeedback || !feedbackText.trim()}
                        onClick={() => void submitFeedback(false)}
                      >
                        Save feedback
                      </Button>
                    </Box>
                  </Stack>
                )}

                {feedbackMessage && <Alert severity="success">{feedbackMessage}</Alert>}
                {feedbackError && <Alert severity="error">{feedbackError}</Alert>}
              </Stack>
            </Paper>
          )}
        </Stack>

        <Box
          mt={2}
          sx={{
            width: 860,
            position: "sticky",
            top: 24,
            alignSelf: "flex-start",
            height: "calc(100vh - 48px)"
          }}
        >
          {file?.preview ? (
            <PdfViewerWithLocation pdfUrl={file.preview} activeField={activeField} />
          ) : (
            <Box
              sx={{
                height: "calc(100vh - 96px)",
                borderRadius: 3,
                backgroundColor: "#f8fafc",
                border: "1px dashed #cbd5e1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3
              }}
            >
              <Typography color="text.secondary">Upload a PDF to preview the agreement here.</Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Page>
  );
}
